import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { getAccountSettings } from "../api";
import { showErrorToast, showSuccessToast } from "@/hooks/use-toast";
import type { AccountSettings } from "../types";

/**
 * Loads the page state and surfaces the outcome of an OAuth round trip, which the
 * BFF callback reports back as a success/error query parameter.
 */
export const useAccountSettings = () => {
  const [settings, setSettings] = useState<AccountSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const hasReportedCallback = useRef(false);

  const refresh = useCallback(async () => {
    try {
      setSettings(await getAccountSettings());
      setLoadError(null);
    } catch {
      setLoadError("We couldn't load your account settings. Please refresh the page.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (hasReportedCallback.current) return;

    const success = searchParams.get("success");
    const error = searchParams.get("error");
    if (!success && !error) return;

    hasReportedCallback.current = true;
    if (success) showSuccessToast(success);
    if (error) showErrorToast(error);

    // Drop the parameters so a refresh doesn't replay the toast.
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  return { settings, isLoading, loadError, refresh };
};
